// CSV/Excel Import Service for Overseas Shipments
// Handles parsing, column mapping, and data validation

export interface ImportedItem {
  name: string;
  quantity: number;
  unit?: string;
  cost?: number; // Rate/Unit
  totalAmount?: number; // Total Amount
  sku?: string;
  serialNumber?: string; // Serial Number
  description?: string; // Description
  [key: string]: any; // For other columns
}

export interface ColumnMapping {
  name: string;
  quantity: string;
  unit?: string;
  cost?: string; // Rate/Unit
  totalAmount?: string; // Total Amount
  sku?: string;
  serialNumber?: string; // Serial Number
  description?: string; // Description
}

/**
 * Parse CSV file content
 */
export function parseCSV(content: string): string[][] {
  const lines: string[][] = [];
  let currentLine: string[] = [];
  let currentField = '';
  let inQuotes = false;

  for (let i = 0; i < content.length; i++) {
    const char = content[i];
    const nextChar = content[i + 1];

    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        // Escaped quote
        currentField += '"';
        i++; // Skip next quote
      } else {
        // Toggle quote state
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      // Field separator
      currentLine.push(currentField.trim());
      currentField = '';
    } else if ((char === '\n' || char === '\r') && !inQuotes) {
      // Line separator
      if (currentField || currentLine.length > 0) {
        currentLine.push(currentField.trim());
        lines.push(currentLine);
        currentLine = [];
        currentField = '';
      }
      // Skip \r\n combination
      if (char === '\r' && nextChar === '\n') {
        i++;
      }
    } else {
      currentField += char;
    }
  }

  // Add last field and line
  if (currentField || currentLine.length > 0) {
    currentLine.push(currentField.trim());
    lines.push(currentLine);
  }

  return lines;
}

/**
 * Auto-detect column mapping from CSV headers
 */
export function autoDetectColumns(headers: string[]): ColumnMapping {
  const mapping: ColumnMapping = {
    name: '',
    quantity: '',
  };

  const lowerHeaders = headers.map(h => h.toLowerCase().trim());

  // Name mapping (prefer name over description)
  const namePatterns = ['name', 'item', 'product', 'item name', 'product name'];
  for (const pattern of namePatterns) {
    const index = lowerHeaders.findIndex(h => h.includes(pattern) && !h.includes('description'));
    if (index !== -1) {
      mapping.name = headers[index];
      break;
    }
  }
  
  // If no name found, try description
  if (!mapping.name) {
    const descIndex = lowerHeaders.findIndex(h => h.includes('description'));
    if (descIndex !== -1) {
      mapping.name = headers[descIndex]; // Use description as name fallback
    }
  }

  // Quantity mapping
  const quantityPatterns = ['quantity', 'qty', 'qty.', 'amount', 'count'];
  for (const pattern of quantityPatterns) {
    const index = lowerHeaders.findIndex(h => h.includes(pattern));
    if (index !== -1) {
      mapping.quantity = headers[index];
      break;
    }
  }

  // Unit mapping
  const unitPatterns = ['unit', 'units', 'uom', 'measure'];
  for (const pattern of unitPatterns) {
    const index = lowerHeaders.findIndex(h => h.includes(pattern));
    if (index !== -1) {
      mapping.unit = headers[index];
      break;
    }
  }

  // Cost mapping
  const costPatterns = ['cost', 'price', 'unit cost', 'unit price', 'rate'];
  for (const pattern of costPatterns) {
    const index = lowerHeaders.findIndex(h => h.includes(pattern));
    if (index !== -1) {
      mapping.cost = headers[index];
      break;
    }
  }

  // SKU mapping
  const skuPatterns = ['sku', 'code', 'product code', 'barcode', 'item code'];
  for (const pattern of skuPatterns) {
    const index = lowerHeaders.findIndex(h => h.includes(pattern));
    if (index !== -1) {
      mapping.sku = headers[index];
      break;
    }
  }

  // Serial Number mapping
  const serialPatterns = ['serial', 'serial number', 'serial no', 'serial#', 's/n'];
  for (const pattern of serialPatterns) {
    const index = lowerHeaders.findIndex(h => h.includes(pattern));
    if (index !== -1) {
      mapping.serialNumber = headers[index];
      break;
    }
  }

  // Description mapping
  const descPatterns = ['description', 'desc', 'notes', 'remarks'];
  for (const pattern of descPatterns) {
    const index = lowerHeaders.findIndex(h => h.includes(pattern));
    if (index !== -1) {
      mapping.description = headers[index];
      break;
    }
  }

  // Total Amount mapping
  const totalAmountPatterns = ['total', 'total amount', 'amount', 'total cost', 'total value'];
  for (const pattern of totalAmountPatterns) {
    const index = lowerHeaders.findIndex(h => h.includes(pattern) && !h.includes('unit'));
    if (index !== -1) {
      mapping.totalAmount = headers[index];
      break;
    }
  }

  return mapping;
}

/**
 * Import items from CSV content
 */
export function importFromCSV(
  content: string,
  mapping: ColumnMapping,
  hasHeader: boolean = true
): ImportedItem[] {
  const lines = parseCSV(content);
  
  if (lines.length === 0) {
    throw new Error('CSV file is empty');
  }

  let startIndex = 0;
  let headers: string[] = [];

  if (hasHeader) {
    headers = lines[0];
    startIndex = 1;
  } else {
    // If no header, use default column names
    headers = mapping.name ? [mapping.name] : [];
    if (mapping.quantity) headers.push(mapping.quantity);
    if (mapping.unit) headers.push(mapping.unit);
    if (mapping.cost) headers.push(mapping.cost);
    if (mapping.sku) headers.push(mapping.sku);
    if (mapping.description) headers.push(mapping.description);
  }

  const items: ImportedItem[] = [];

  for (let i = startIndex; i < lines.length; i++) {
    const line = lines[i];
    
    // Skip empty lines
    if (line.every(cell => !cell.trim())) {
      continue;
    }

    const item: ImportedItem = {
      name: '',
      quantity: 0,
    };

    // Map columns - use description as name if name column not found
    const nameIndex = headers.indexOf(mapping.name);
    if (nameIndex !== -1 && line[nameIndex]) {
      item.name = line[nameIndex].trim();
    } else if (mapping.description) {
      // If no name column but description exists, use description as name
      const descIndex = headers.indexOf(mapping.description);
      if (descIndex !== -1 && line[descIndex]) {
        item.name = line[descIndex].trim();
      }
    }

    const quantityIndex = headers.indexOf(mapping.quantity);
    if (quantityIndex !== -1 && line[quantityIndex]) {
      const qty = parseFloat(line[quantityIndex].replace(/[^0-9.-]/g, ''));
      item.quantity = isNaN(qty) ? 0 : qty;
    }

    if (mapping.unit) {
      const unitIndex = headers.indexOf(mapping.unit);
      if (unitIndex !== -1 && line[unitIndex]) {
        item.unit = line[unitIndex].trim();
      }
    }

    if (mapping.cost) {
      const costIndex = headers.indexOf(mapping.cost);
      if (costIndex !== -1 && line[costIndex]) {
        const cost = parseFloat(line[costIndex].replace(/[^0-9.-]/g, ''));
        item.cost = isNaN(cost) ? undefined : cost;
      }
    }

    if (mapping.totalAmount) {
      const totalIndex = headers.indexOf(mapping.totalAmount);
      if (totalIndex !== -1 && line[totalIndex]) {
        const total = parseFloat(line[totalIndex].replace(/[^0-9.-]/g, ''));
        item.totalAmount = isNaN(total) ? undefined : total;
      }
    }

    if (mapping.sku) {
      const skuIndex = headers.indexOf(mapping.sku);
      if (skuIndex !== -1 && line[skuIndex]) {
        item.sku = line[skuIndex].trim();
      }
    }

    if (mapping.serialNumber) {
      const serialIndex = headers.indexOf(mapping.serialNumber);
      if (serialIndex !== -1 && line[serialIndex]) {
        item.serialNumber = line[serialIndex].trim();
      }
    }

    if (mapping.description) {
      const descIndex = headers.indexOf(mapping.description);
      if (descIndex !== -1 && line[descIndex]) {
        item.description = line[descIndex].trim();
      }
    }

    // Only add if name and quantity are valid
    if (item.name && item.quantity > 0) {
      items.push(item);
    }
  }

  return items;
}

/**
 * Validate imported items
 */
export function validateImportedItems(items: ImportedItem[]): {
  valid: ImportedItem[];
  invalid: { item: ImportedItem; reason: string }[];
} {
  const valid: ImportedItem[] = [];
  const invalid: { item: ImportedItem; reason: string }[] = [];

  for (const item of items) {
    if (!item.name || item.name.trim() === '') {
      invalid.push({ item, reason: 'Missing item name' });
      continue;
    }

    if (!item.quantity || item.quantity <= 0) {
      invalid.push({ item, reason: 'Invalid quantity (must be > 0)' });
      continue;
    }

    valid.push(item);
  }

  return { valid, invalid };
}

/**
 * Read file as text (for CSV)
 */
export function readFileAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      resolve(e.target?.result as string);
    };
    reader.onerror = (e) => {
      reject(new Error('Failed to read file'));
    };
    reader.readAsText(file);
  });
}
